'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetThesesRow, useGetThesesInfinite, useGetUserActivityInfinite, usePrefetchGetTheses, usePrefetchGetUserActivity } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useId, useState } from 'react';
import { PortfolioListTransition } from '@/components/portfolio/positions-card/portfolio-list-transition';
import { PortfolioPanelHeader } from '@/components/portfolio/shared/portfolio-panel-header';
import { Edit03IconSolid } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { useViewportActivation } from '@/hooks/ui/use-viewport-activation';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { ACTIVITY_FEED_FIELDS } from '@/lib/sdk/projections';
import { logger } from '@/lib/telemetry/logger';
import { cn } from '@/lib/ui/utils';
import { isActivityAttributedToWallet, isThesisAttributedToWallet } from '@/lib/wallet/multi-wallet-scope';
import { PortfolioActivityCount } from './portfolio-activity-count';
import { PortfolioActivityFeed } from './portfolio-activity-feed';
import { ACTIVITY_ICONS, ACTIVITY_QUERY_FILTERS, type ActivityFilter, matchesActivityFilter } from './portfolio-activity-filter';
import { PortfolioThesesContent } from './portfolio-theses-content';
export function PortfolioActivityPanel(t0) {
  const $ = _c(117);
  const {
    userId,
    walletPubkeys,
    giftUserIds,
    isOwn,
    attributionWalletAddress,
    className
  } = t0;
  const t = useTranslations("profile.stats");
  const {
    ref: panelRef,
    enabled,
    activate
  } = useViewportActivation();
  const id = useId();
  const [view, setView] = useState("theses");
  const prefetchTheses = usePrefetchGetTheses();
  const prefetchActivity = usePrefetchGetUserActivity();
  const giftingGate = useFeatureGate("gifting");
  const giftsEnabled = isOwn && giftingGate === true;
  const activeView = view === "gifts" && !giftsEnabled ? "theses" : view;
  let t1;
  if ($[0] !== userId || $[1] !== walletPubkeys) {
    t1 = {
      authorId: userId,
      walletPubkeys,
      sort: "recent"
    };
    $[0] = userId;
    $[1] = walletPubkeys;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = enabled && activeView === "theses";
  let t3;
  if ($[3] !== t2) {
    t3 = {
      enabled: t2
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const theses = useGetThesesInfinite(t1, t3);
  let t4;
  if ($[5] !== giftUserIds || $[6] !== giftsEnabled) {
    t4 = giftsEnabled ? giftUserIds : [];
    $[5] = giftUserIds;
    $[6] = giftsEnabled;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== activeView) {
    t5 = activeView === "theses" ? {} : ACTIVITY_QUERY_FILTERS[activeView];
    $[8] = activeView;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [...ACTIVITY_FEED_FIELDS];
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== userId || $[14] !== walletPubkeys) {
    t7 = {
      userId,
      walletPubkeys,
      giftUserIds: t4,
      ...t5,
      fields: t6
    };
    $[11] = t4;
    $[12] = t5;
    $[13] = userId;
    $[14] = walletPubkeys;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = enabled && activeView !== "theses";
  let t9;
  if ($[16] !== t8) {
    t9 = {
      enabled: t8
    };
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const activity = useGetUserActivityInfinite(t7, t9);
  let t10;
  let t11;
  if ($[18] !== theses.error) {
    t10 = () => {
      if (theses.error) {
        logger.warn("portfolio.theses.load_failed", {
          errorName: theses.error.name
        });
      }
    };
    t11 = [theses.error];
    $[18] = theses.error;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  useEffect(t10, t11);
  const thesisRows = useLiveListData(theses.queryKey, theses.rows);
  let t12;
  if ($[21] !== attributionWalletAddress || $[22] !== thesisRows) {
    let t13;
    if ($[24] !== attributionWalletAddress) {
      t13 = row => attributionWalletAddress === null || isThesisAttributedToWallet(row, attributionWalletAddress);
      $[24] = attributionWalletAddress;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    t12 = thesisRows.filter(t13);
    $[21] = attributionWalletAddress;
    $[22] = thesisRows;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const visibleTheses = t12;
  const selectedQuery = activeView === "theses" ? theses : activity;
  let t13;
  if ($[26] !== activeView || $[27] !== activity || $[28] !== attributionWalletAddress || $[29] !== visibleTheses) {
    t13 = activeView === "theses" ? visibleTheses.length : activity.rows.filter(row_0 => matchesActivityFilter(row_0, activeView) && (attributionWalletAddress === null || isActivityAttributedToWallet(row_0, attributionWalletAddress))).length;
    $[26] = activeView;
    $[27] = activity;
    $[28] = attributionWalletAddress;
    $[29] = visibleTheses;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const count = t13;
  let t14;
  if ($[31] !== activeView || $[32] !== giftUserIds || $[33] !== giftsEnabled || $[34] !== prefetchActivity || $[35] !== prefetchTheses || $[36] !== userId || $[37] !== walletPubkeys) {
    t14 = function prefetchSegment(next) {
      if (next === activeView) {
        return;
      }
      if (next === "theses") {
        prefetchTheses({
          authorId: userId,
          walletPubkeys,
          sort: "recent"
        });
      } else {
        if (Object.hasOwn(ACTIVITY_QUERY_FILTERS, next) && (next !== "gifts" || giftsEnabled)) {
          prefetchActivity({
            userId,
            walletPubkeys,
            giftUserIds: giftsEnabled ? giftUserIds : [],
            ...ACTIVITY_QUERY_FILTERS[next as ActivityFilter],
            fields: [...ACTIVITY_FEED_FIELDS]
          });
        }
      }
    };
    $[31] = activeView;
    $[32] = giftUserIds;
    $[33] = giftsEnabled;
    $[34] = prefetchActivity;
    $[35] = prefetchTheses;
    $[36] = userId;
    $[37] = walletPubkeys;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const prefetchSegment = t14;
  let t15;
  if ($[39] !== className) {
    t15 = cn("min-w-0", className);
    $[39] = className;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t) {
    t16 = t("activity_title");
    $[41] = t;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const t17 = !enabled || selectedQuery.isPending || selectedQuery.isError || selectedQuery.isPlaceholderData;
  let t18;
  if ($[43] !== count || $[44] !== selectedQuery.hasNextPage || $[45] !== t17) {
    t18 = <PortfolioActivityCount count={count} hasMore={selectedQuery.hasNextPage} pending={t17} />;
    $[43] = count;
    $[44] = selectedQuery.hasNextPage;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t16 || $[48] !== t18) {
    t19 = <PortfolioPanelHeader title={<span className="inline-flex items-center gap-2">{t16}{t18}</span>} />;
    $[47] = t16;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t) {
    t20 = t("theses_title");
    $[50] = t;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t20) {
    t21 = {
      key: "theses",
      label: t20,
      icon: Edit03IconSolid
    };
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t) {
    t22 = t("activity_tabs.buys");
    $[54] = t;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== t22) {
    t23 = {
      key: "buys",
      label: t22,
      icon: ACTIVITY_ICONS.buys
    };
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t) {
    t24 = t("activity_tabs.sells");
    $[58] = t;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t24) {
    t25 = {
      key: "sells",
      label: t24,
      icon: ACTIVITY_ICONS.sells
    };
    $[60] = t24;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== t) {
    t26 = t("activity_tabs.backings");
    $[62] = t;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t26) {
    t27 = {
      key: "backings",
      label: t26,
      icon: ACTIVITY_ICONS.backings
    };
    $[64] = t26;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== t) {
    t28 = t("activity_tabs.claims");
    $[66] = t;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== t28) {
    t29 = {
      key: "claims",
      label: t28,
      icon: ACTIVITY_ICONS.claims
    };
    $[68] = t28;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] !== t) {
    t30 = t("activity_tabs.boosts");
    $[70] = t;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== t30) {
    t31 = {
      key: "boosts",
      label: t30,
      icon: ACTIVITY_ICONS.boosts
    };
    $[72] = t30;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== t) {
    t32 = t("activity_tabs.gifts");
    $[74] = t;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  const t33 = !giftsEnabled;
  let t34;
  if ($[76] !== t32 || $[77] !== t33) {
    t34 = {
      key: "gifts",
      label: t32,
      icon: ACTIVITY_ICONS.gifts,
      hidden: t33
    };
    $[76] = t32;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== t21 || $[80] !== t23 || $[81] !== t25 || $[82] !== t27 || $[83] !== t29 || $[84] !== t31 || $[85] !== t34) {
    t35 = [t21, t23, t25, t27, t29, t31, t34];
    $[79] = t21;
    $[80] = t23;
    $[81] = t25;
    $[82] = t27;
    $[83] = t29;
    $[84] = t31;
    $[85] = t34;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] !== activate || $[88] !== giftsEnabled) {
    t36 = next_0 => {
      activate();
      if (next_0 === "theses") {
        setView(next_0);
      } else {
        if (Object.hasOwn(ACTIVITY_QUERY_FILTERS, next_0) && (next_0 !== "gifts" || giftsEnabled)) {
          setView(next_0 as ActivityFilter);
        }
      }
    };
    $[87] = activate;
    $[88] = giftsEnabled;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  const t37 = `${id}-activity`;
  let t38;
  if ($[90] !== t) {
    t38 = t("activity_title");
    $[90] = t;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  let t39;
  if ($[92] !== activeView || $[93] !== prefetchSegment || $[94] !== t35 || $[95] !== t36 || $[96] !== t37 || $[97] !== t38) {
    t39 = <div className="-mx-4 min-w-0 overflow-x-auto px-4 scrollbar-none"><SegmentedControl items={t35} active={activeView} onChange={t36} onItemPointerEnter={prefetchSegment} layoutId={t37} variant="tab" indicatorClassName="bg-background-tertiary dark:bg-background-tertiary" asTabs={true} ariaLabel={t38} /></div>;
    $[92] = activeView;
    $[93] = prefetchSegment;
    $[94] = t35;
    $[95] = t36;
    $[96] = t37;
    $[97] = t38;
    $[98] = t39;
  } else {
    t39 = $[98];
  }
  let t40;
  if ($[99] !== activeView || $[100] !== activity || $[101] !== attributionWalletAddress || $[102] !== isOwn || $[103] !== theses || $[104] !== visibleTheses) {
    t40 = activeView === "theses" ? <PortfolioThesesContent query={theses} rows={visibleTheses} isOwn={isOwn} /> : <PortfolioActivityFeed key={activeView} query={activity} isOwn={isOwn} attributionWalletAddress={attributionWalletAddress} filter={activeView} />;
    $[99] = activeView;
    $[100] = activity;
    $[101] = attributionWalletAddress;
    $[102] = isOwn;
    $[103] = theses;
    $[104] = visibleTheses;
    $[105] = t40;
  } else {
    t40 = $[105];
  }
  let t41;
  if ($[106] !== activeView || $[107] !== t40) {
    t41 = <PortfolioListTransition view={activeView}>{t40}</PortfolioListTransition>;
    $[106] = activeView;
    $[107] = t40;
    $[108] = t41;
  } else {
    t41 = $[108];
  }
  let t42;
  if ($[109] !== t19 || $[110] !== t39 || $[111] !== t41) {
    t42 = <PanelContent className="flex flex-col gap-4">{t19}{t39}{t41}</PanelContent>;
    $[109] = t19;
    $[110] = t39;
    $[111] = t41;
    $[112] = t42;
  } else {
    t42 = $[112];
  }
  let t43;
  if ($[113] !== panelRef || $[114] !== t15 || $[115] !== t42) {
    t43 = <Panel ref={panelRef} className={t15} data-testid="portfolio-activity">{t42}</Panel>;
    $[113] = panelRef;
    $[114] = t15;
    $[115] = t42;
    $[116] = t43;
  } else {
    t43 = $[116];
  }
  return t43;
}